import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { guestPortalApi, AddHelpfullPayload } from "./api";

// --- Tips Hooks ---
export const useUpdateTipsStatus = () => {
  return useSmartMutation(
    (payload: AddHelpfullPayload) =>
      guestPortalApi.addHelpfulTips(payload)
  );
};

export const useTipsData = (params: {
  remote_utility_id: string;
  utility_service: any;
  show_inactive: any;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.usage.tipsData(params),
    () => guestPortalApi.getTipsData(params)
  );
};

// --- Offices Hooks ---
export const useServiceDetail = (params: {
  remote_utility_id: string;
  consumer_id: string;
  search_data?: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.contacts.cotactDetail(params),
    () => guestPortalApi.getServiceDetail(params)
  );
};

// --- FAQs Hooks ---
export const useFaqData = (params: {
  remote_utility_id: string;
  show_inactive: any;
  faq_category: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.faqs.faqData(params),
    () => guestPortalApi.getFaq(params)
  );
};

export const useFaqSearch = (params: {
  search_data: string;
  remote_utility_id: string;  
}, options?: { enabled?: boolean }) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.faqs.searchFaq(params),
    () => guestPortalApi.searchFaq(params),
    {
      enabled: options?.enabled !== false && params.search_data.length > 0,
      staleTime: 30000,
    }
  );
};

export const useUpdateFaqStatus = () => {
  return useSmartMutation(
    (payload: AddHelpfullPayload) =>
      guestPortalApi.addHelpfulFaq(payload)
  );
};

// --- Billing Hooks ---
export const usePendingBills = (
  params: {
    remote_utility_id: string;
    consumer_number: string;
  },
  options?: { enabled?: boolean }
) => {
  const utility_service = localStorage.getItem('guest_portal_utility_service') || undefined;

  return useSmartQuery(
    ['cx', 'billing', 'pendingBills', { ...params }] as any,
    () => guestPortalApi.getPendingBills({ ...params }),
    {
      enabled: options?.enabled !== false && !!params.consumer_number,
    }
  );
};

// --- Service Request Hooks ---
export const useUtilityRequestConfiguration = (
  params: {
    remote_utility_id: string;
    request_type: string;
    disable_pagination: boolean;
  },
  options?: { enabled?: boolean }
) => {
  return useSmartQuery(
    ['cx', 'serviceRequest', 'utilityRequestConfiguration', { ...params }] as any,
    () => guestPortalApi.getUtilityRequestConfiguration({ ...params }),
    {
      enabled: options?.enabled !== false && !!params.remote_utility_id,
    }
  );
};

// --- Consumer Onboarding Hooks ---
export const useCategory = (params: { remote_utility_id?: string }) => {
  return useSmartQuery(
    ['cx', 'consumerOnboarding', 'category', params] as any,
    () => guestPortalApi.getCategory(params),
  );
};

export const useSubCategory = (params: { remote_utility_id?: string, code_list: any }) => {
  return useSmartQuery(
    ['cx', 'consumerOnboarding', 'subCategory', params] as any,
    () => guestPortalApi.getSubCategory(params),
    { enabled: !!params.code_list }
  );
};

export const useCity = (params: { remote_utility_id?: string }) => {
  return useSmartQuery(
    ['cx', 'consumerOnboarding', 'city', params] as any,
    () => guestPortalApi.getCity(params),
  );
};

export const useOwnerType = () => {
  return useSmartQuery(
    ['cx', 'consumerOnboarding', 'ownerType'] as any,
    () => guestPortalApi.getOwnerType(),
  );
};

export const useUtilityServices = (params: { remote_utility_id?: string }) => {
  return useSmartQuery(
    ['cx', 'consumerOnboarding', 'utilityServices', params] as any,
    () => guestPortalApi.getUtilityServices(params),
  );
};

export const useConsumerRelation = () => {
  return useSmartQuery(
    ['cx', 'consumerOnboarding', 'consumerRelation'] as any,
    () => guestPortalApi.getConsumerRelation(),
  );
};

export const useAddConsumer = () => {
  return useSmartMutation(
    (payload: any) => guestPortalApi.addConsumer(payload),
  );
};

export const useUpdateConsumer = () => {
  return useSmartMutation(
    ({ consumerId, payload }: { consumerId: string; payload: any }) =>
      guestPortalApi.updateConsumer(consumerId, payload)
  );
};

export const useConsumerDetail = (params: { consumer_id: any, remote_utility_id?: string }) => {
  return useSmartQuery(
    ['cx', 'consumerOnboarding', 'consumerDetail', params] as any,
    () => guestPortalApi.getConsumerDetail(params),
    {
      enabled: !!params.consumer_id, 
    }
  );
};

export const useApplicationStatus = (params: { consumer_no: string, remote_utility_id?: string }, options?: { enabled?: boolean }) => {
  return useSmartQuery(
    ['cx', 'consumerOnboarding', 'applicationStatus', params] as any,
    () => guestPortalApi.getApplicationStatus(params),
    {
      enabled: options?.enabled !== false && !!params.consumer_no,
    }
  );
};
export const useGuestPortalBranding = (params: any, options?: any) => {
  return useSmartQuery(
    ['cx', 'guestPortalBranding', params] as any,
    () => guestPortalApi.getGuestPortalBranding(params),
    options
  );
};

// --- Master Account Search Hook ---
export const useSearchMasterConsumers = (
  params: {
    remote_utility_id: string;
    search_data: string;
  },
  options?: { enabled?: boolean }
) => {
  return useSmartQuery(
    ['cx', 'consumerOnboarding', 'masterSearch', { ...params }] as any,
    () => guestPortalApi.searchMasterConsumers({ ...params }),
    {
      enabled: options?.enabled ?? false,
    }
  );
};
